import type { PlayerState } from "../../model/PlayerState.js";
import type { Roll } from "../../model/dice/Roll.js";
import { ActionGameState } from "./ActionGameState.js";
import type { Move } from "../../model/Move.js";
import type { Board } from "../../model/Board.js";
import type { PlayerType } from "../../model/PlayerType.js";
import type {
    DescribePlayerNames } from "./GameState.js";
import {
    getDescribePlayerName,
} from "./GameState.js";
import { isSameRuntimeType } from "../../../util/tsutils.js";


/**
 * A game state that represents a move of a piece on the board.
 */
export class MovedGameState extends ActionGameState {
    private readonly roll: Roll;
    private readonly move: Move;

    constructor(
        board: Board,
        lightPlayer: PlayerState,
        darkPlayer: PlayerState,
        timeSinceGameStartMs: number,
        turn: PlayerType,
        roll: Roll,
        move: Move,
    ) {
        super(board, lightPlayer, darkPlayer, timeSinceGameStartMs, turn);
        this.roll = roll;
        this.move = move;
    }

    getRoll(): Roll {
        return this.roll;
    }

    getMove(): Move {
        return this.move;
    }

    override describe(playerNames?: DescribePlayerNames): string {
        const playerName = getDescribePlayerName(this.getTurn(), playerNames);
        const prefix = `${playerName} `;

        const introducing = this.move.isIntroduction();
        const scoring = this.move.isScore();

        if (introducing && scoring) {
            return prefix + "scored a newly introduced piece";
        } else if (scoring) {
            return prefix + "scored their " + this.move.getSource().toString() + " piece";
        } else if (introducing) {
            return prefix + "introduced a piece to " + this.move.getDest().toString();
        } else {
            return prefix + "moved their "
                + this.move.getSource().toString() + " piece to "
                + (this.move.isCapture() ? "capture " : "")
                + this.move.getDest().toString();
        }
    }

    override equals(other: unknown): boolean {
        if (other === this)
            return true;
        if (!isSameRuntimeType(this, other))
            return false;

        return ActionGameState._baselineEquals(this, other)
            && this.roll.equals(other.roll)
            && this.move.equals(other.move);
    }
}
